import {generateKpi} from '@domain/shifthandover'
import {ManualKpiToUpdate} from '@hconnect/common/types'
import {
  Button,
  ControlledInput,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle
} from '@hm/fundamental'
import {mdiCheck, mdiWindowClose} from '@mdi/js'
import {TFunction} from 'i18next'
import React, {FC} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {requiredValidator, uniqueTitleValidator} from '../../validators'

type ManualKpiCreateDialog = {
  isDialogOpen: boolean
  onSave: (data: ManualKpiToUpdate) => void
  onClose: () => void
  existingKpiNames: string[]
}

const titleValidate = (t: TFunction, existingTitles: string[]) => ({
  ...requiredValidator(t),
  validate: uniqueTitleValidator(t, existingTitles)
})

export const ManualKpiCreateDialog: FC<ManualKpiCreateDialog> = ({
  isDialogOpen,
  onClose,
  onSave,
  existingKpiNames
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {control, handleSubmit, reset} = useForm<Partial<ManualKpiToUpdate>>()

  const handleClose = () => {
    onClose()
    reset()
  }

  const onSubmit = (data: Partial<ManualKpiToUpdate>) => {
    const newKpi = generateKpi(data)
    onSave(newKpi)
    handleClose()
  }

  return (
    <Modal open={isDialogOpen} onClose={handleClose} data-cy="manual-kpi-create-dialog">
      <ModalTitle data-cy="manual-kpi-create-title">{t('performance.label.enterKpi')}</ModalTitle>
      <ModalContent>
        <div className="flex flex-col gap-4">
          <Controller
            control={control}
            name="name"
            rules={titleValidate(t, existingKpiNames)}
            render={({field: {ref, value, onChange}, fieldState: {error}}) => (
              <ControlledInput
                ref={ref}
                label={t('performance.label.title')}
                value={value ?? ''}
                onChange={onChange}
                error={error?.message}
                variant="small"
                data-cy="manual-kpi-create-name"
              />
            )}
          />
          <Controller
            control={control}
            name="unit"
            rules={requiredValidator(t)}
            render={({field: {ref, value, onChange}, fieldState: {error}}) => (
              <ControlledInput
                ref={ref}
                label={t('performance.label.unit')}
                value={value ?? ''}
                onChange={onChange}
                error={error?.message}
                variant="small"
                data-cy="manual-kpi-create-unit"
              />
            )}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <Button
          icon={mdiWindowClose}
          variant="secondary"
          onClick={handleClose}
          data-cy="manual-kpi-create-close-button"
        >
          {t('action.cancel')}
        </Button>
        <Button
          icon={mdiCheck}
          onClick={() => void handleSubmit(onSubmit)()}
          data-cy="manual-kpi-create-save-button"
        >
          {t('action.save')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
